import { Injectable, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { TranslationService, SupportedLang } from './translation.service';

/**
 * Keeps Angular Material datepickers in sync with the app language:
 * - DateAdapter locale (month/day names, input formatting)
 * - MatDatepickerIntl labels (calendar a11y strings)
 */
@Injectable({ providedIn: 'root' })
export class MaterialDateLocaleService {
  private readonly adapter = inject(DateAdapter<Date>);
  private readonly datepickerIntl = inject(MatDatepickerIntl);
  private readonly translation = inject(TranslationService);
  private started = false;

  /** Call once at app bootstrap. */
  init(): void {
    if (this.started) return;
    this.started = true;

    this.applyLocale(this.translation.currentLang);
    this.translation.currentLang$.subscribe((lang) => this.applyLocale(lang));
  }

  private applyLocale(lang: SupportedLang): void {
    // 'km' uses Khmer month/day names via Intl; 'en' stays US-style numeric input.
    this.adapter.setLocale(lang === 'km' ? 'km' : 'en-US');
    this.applyDatepickerLabels();
  }

  private applyDatepickerLabels(): void {
    const t = (key: string) => this.translation.translate(key);

    this.datepickerIntl.calendarLabel = t('datepicker.calendar');
    this.datepickerIntl.openCalendarLabel = t('datepicker.openCalendar');
    this.datepickerIntl.closeCalendarLabel = t('datepicker.closeCalendar');
    this.datepickerIntl.prevMonthLabel = t('datepicker.prevMonth');
    this.datepickerIntl.nextMonthLabel = t('datepicker.nextMonth');
    this.datepickerIntl.prevYearLabel = t('datepicker.prevYear');
    this.datepickerIntl.nextYearLabel = t('datepicker.nextYear');
    this.datepickerIntl.prevMultiYearLabel = t('datepicker.prevMultiYear');
    this.datepickerIntl.nextMultiYearLabel = t('datepicker.nextMultiYear');
    this.datepickerIntl.switchToMonthViewLabel = t('datepicker.chooseDate');
    this.datepickerIntl.switchToMultiYearViewLabel = t('datepicker.chooseMonthYear');
    this.datepickerIntl.startDateLabel = t('datepicker.startDate');
    this.datepickerIntl.endDateLabel = t('datepicker.endDate');

    this.datepickerIntl.changes.next();
  }
}
